import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const AforoUsersTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [aforoUsers, setAforoUsers] = useState([]);
  const [showAforoForm, setShowAforoForm] = useState(false);
  const [editingAforoUser, setEditingAforoUser] = useState(null);
  const [aforoForm, setAforoForm] = useState({
    username: "",
    password: "",
    nombre: "",
    email: "",
    eventos_asignados: []
  });

  const fetchAforoUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/aforo-users`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setAforoUsers(response.data);
    } catch (e) {
      console.error("Error fetching aforo users:", e);
    }
  }, [credentials]);

  useEffect(() => {
    fetchAforoUsers();
  }, [fetchAforoUsers]);

  return (
    <>
          <>
            {/* Banner to go to Aforo Panel */}
            <div 
              onClick={() => window.open('/aforo', '_blank')}
              className="w-full mb-8 p-4 bg-[var(--accent-cyan)]/20 border-2 border-[var(--accent-cyan)] cursor-pointer hover:bg-[var(--accent-cyan)]/30 transition-all"
              data-testid="go-to-aforo-banner"
            >
              <div className="flex items-center justify-center gap-3 text-[var(--accent-cyan)] font-bold text-lg uppercase tracking-wider">
                <Users size={24} />
                Ir al Panel de Aforo
                <ChevronRight size={24} />
              </div>
              <p className="text-center text-[var(--text-muted)] text-sm mt-2">
                Ver estadísticas en tiempo real
              </p>
            </div>

            {/* Add Aforo User Button */}
            {!showAforoForm && (
              <button 
                onClick={() => setShowAforoForm(true)}
                className="btn-download mb-8 w-auto px-6"
                data-testid="add-aforo-btn"
              >
                <UserPlus size={18} className="inline mr-2" />
                Nuevo Usuario Control Aforo
              </button>
            )}

            {/* Aforo Form */}
            {showAforoForm && (
              <div className="form-section mb-8 max-w-lg">
                <h3 className="form-title mb-6">{editingAforoUser ? 'Editar Usuario Control Aforo' : 'Nuevo Usuario Control Aforo'}</h3>
                <p className="text-[var(--text-muted)] text-sm mb-4">
                  Este usuario solo podrá ver las estadísticas de los eventos asignados.
                </p>
                
                {error && <div className="error-message mb-4">{error}</div>}
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setLoading(true);
                  setError("");
                  try {
                    if (editingAforoUser) {
                      // Update existing user
                      const updateData = {
                        nombre: aforoForm.nombre,
                        email: aforoForm.email,
                        eventos_asignados: aforoForm.eventos_asignados
                      };
                      if (aforoForm.password) {
                        updateData.password = aforoForm.password;
                      }
                      await axios.put(`${API}/admin/aforo-users/${editingAforoUser.id}`, updateData, {
                        headers: { Authorization: `Basic ${credentials}` }
                      });
                    } else {
                      // Create new user
                      await axios.post(`${API}/admin/aforo-users`, aforoForm, {
                        headers: { Authorization: `Basic ${credentials}` }
                      });
                    }
                    setShowAforoForm(false);
                    setEditingAforoUser(null);
                    setAforoForm({ username: "", password: "", nombre: "", email: "", eventos_asignados: [] });
                    fetchAforoUsers();
                  } catch (err) {
                    setError(err.response?.data?.detail || "Error al guardar usuario");
                  } finally {
                    setLoading(false);
                  }
                }}>
                  <div className="form-group">
                    <label className="form-label">Nombre de Usuario</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={aforoForm.username}
                      onChange={(e) => setAforoForm(prev => ({ ...prev, username: e.target.value }))}
                      required={!editingAforoUser}
                      disabled={!!editingAforoUser}
                      data-testid="aforo-username"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{editingAforoUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}</label>
                    <PasswordInput
                      value={aforoForm.password}
                      onChange={(e) => setAforoForm(prev => ({ ...prev, password: e.target.value }))}
                      required={!editingAforoUser}
                      minLength={editingAforoUser ? 0 : 6}
                      testId="aforo-password"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre Completo</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={aforoForm.nombre}
                      onChange={(e) => setAforoForm(prev => ({ ...prev, nombre: e.target.value }))}
                      required
                      data-testid="aforo-nombre"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email para Alertas</label>
                    <input
                      type="email"
                      className="custom-input"
                      placeholder="email@ejemplo.com"
                      value={aforoForm.email}
                      onChange={(e) => setAforoForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="aforo-email"
                    />
                    <p className="text-[var(--text-muted)] text-xs mt-1">
                      Recibirá alertas cuando queden 50 entradas en un tramo
                    </p>
                  </div>
                  
                  {/* Eventos Asignados */}
                  <div className="form-group">
                    <label className="form-label">Eventos Asignados</label>
                    <p className="text-[var(--text-muted)] text-xs mb-2">
                      Selecciona los eventos que este usuario podrá ver
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto bg-[var(--bg)] p-3">
                      {events.map(event => (
                        <label key={event.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-[var(--surface)]">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-[var(--accent-cyan)]"
                            checked={aforoForm.eventos_asignados.includes(event.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAforoForm(prev => ({
                                  ...prev,
                                  eventos_asignados: [...prev.eventos_asignados, event.id]
                                }));
                              } else {
                                setAforoForm(prev => ({
                                  ...prev,
                                  eventos_asignados: prev.eventos_asignados.filter(id => id !== event.id)
                                }));
                              }
                            }}
                          />
                          <span className="text-white text-sm">{event.nombre}</span>
                          <span className="text-[var(--text-muted)] text-xs">({event.fecha})</span>
                        </label>
                      ))}
                    </div>
                    {aforoForm.eventos_asignados.length === 0 && (
                      <p className="text-orange-400 text-xs mt-2">
                        ⚠️ Sin eventos asignados, el usuario no podrá ver ninguna estadística
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-4 mt-6">
                    <button type="submit" className="btn-download" disabled={loading} data-testid="aforo-submit-btn">
                      {loading ? <span className="spinner"></span> : (editingAforoUser ? "Guardar Cambios" : "Crear Usuario")}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowAforoForm(false);
                        setEditingAforoUser(null);
                        setAforoForm({ username: "", password: "", nombre: "", eventos_asignados: [] });
                      }}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Info Box */}
            <div className="mb-6 p-4 bg-[var(--surface)] border-l-4 border-[var(--accent-cyan)]">
              <p className="text-[var(--accent-cyan)] font-bold mb-2">
                <Eye size={16} className="inline mr-2" />
                URL de Acceso para Control de Aforo
              </p>
              <p className="text-[var(--text-muted)] text-sm mb-2">
                Los usuarios de control de aforo pueden acceder a las estadísticas en:
              </p>
              <code className="bg-[var(--bg)] px-3 py-2 block text-[var(--accent-cyan)] text-sm">
                {BACKEND_URL.replace('/api', '')}/aforo
              </code>
            </div>

            {/* Aforo Users List */}
            <div className="form-section">
              <h3 className="form-title mb-4">Usuarios de Control de Aforo</h3>
              
              {aforoUsers.length === 0 ? (
                <p className="text-[var(--text-muted)] text-center py-8">
                  No hay usuarios de control de aforo creados
                </p>
              ) : (
                <div className="space-y-4">
                  {aforoUsers.map(user => {
                    const eventosAsignados = user.eventos_asignados || [];
                    const eventosNombres = eventosAsignados.map(id => {
                      const evento = events.find(e => e.id === id);
                      return evento ? evento.nombre : id;
                    });
                    
                    return (
                      <div 
                        key={user.id} 
                        className="admin-event-card"
                        data-testid={`aforo-user-${user.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-[var(--accent-cyan)] flex items-center justify-center">
                              <Eye size={24} className="text-black" />
                            </div>
                            <div>
                              <h4 className="text-white font-bold">{user.nombre}</h4>
                              <p className="text-[var(--text-muted)] text-sm">@{user.username}</p>
                              {user.email && (
                                <p className="text-[var(--primary)] text-xs">
                                  📧 {user.email}
                                </p>
                              )}
                              {eventosNombres.length > 0 ? (
                                <p className="text-[var(--accent-cyan)] text-xs mt-1">
                                  Eventos: {eventosNombres.join(", ")}
                                </p>
                              ) : (
                                <p className="text-orange-400 text-xs mt-1">
                                  ⚠️ Sin eventos asignados
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setEditingAforoUser(user);
                                setAforoForm({
                                  username: user.username,
                                  password: "",
                                  nombre: user.nombre,
                                  email: user.email || "",
                                  eventos_asignados: user.eventos_asignados || []
                                });
                                setShowAforoForm(true);
                              }}
                              className="p-2 text-[var(--text-muted)] hover:text-[var(--accent-cyan)] transition-colors"
                              title="Editar usuario"
                              data-testid={`edit-aforo-${user.id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`¿Eliminar al usuario ${user.nombre}?`)) {
                                  try {
                                    await axios.delete(`${API}/admin/aforo-users/${user.id}`, {
                                      headers: { Authorization: `Basic ${credentials}` }
                                    });
                                    fetchAforoUsers();
                                  } catch (err) {
                                    setError("Error al eliminar usuario");
                                  }
                                }
                              }}
                              className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                              title="Eliminar usuario"
                              data-testid={`delete-aforo-${user.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>

    </>
  );
};

export default AforoUsersTab;
